import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, UserCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/auth/hooks/useAuth';
import type { LitSquadInventoryEntry } from '@/lib/api';
import { isSafeImageUri, resolveLitSquadThumbnail } from '../utils';
import type { UseAvatarEditorStateReturn } from '../hooks/useAvatarEditorState';
import type { UseAvatarUploadReturn } from '../hooks/useAvatarUpload';
import { AvatarEditorAccessoryChip } from './AvatarEditorAccessoryChip';

const FULL_SIZE_PX = 512;
const MINI_INTERMEDIATE_PX = 256;
const MINI_SIZE_PX = 128;

const CDN_BASE_URL: string = import.meta.env.VITE_DATA_STORAGE_CDN_URL ?? '';

export interface AvatarEditorProps {
  /**
   * Editor state — owned by the parent so the inventory grid's "Set as
   * avatar" / "Add accessory" buttons can drive the same state.
   */
  editor: UseAvatarEditorStateReturn;
  /** Upload mutation — also owned by the parent so other pages could observe it. */
  upload: UseAvatarUploadReturn;
  /**
   * The user's owned items. Source of truth for resolving image URIs from
   * the IDs the editor state holds. Pass the same list rendered in the
   * inventory grid so adding from the grid lights up immediately.
   */
  items: readonly LitSquadInventoryEntry[];
}

/**
 * Loads an `<img>` cross-origin so the resulting bitmap can be drawn to a
 * canvas without tainting it. Resolves with the loaded image, or rejects
 * if the source fails to load (incl. CORS-blocked).
 */
function loadCrossOriginImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Returns a Promise<Blob> for a canvas. Wraps the callback `toBlob` API
 * in a Promise and rejects if the canvas was tainted (`toBlob` returns
 * null in that case).
 */
function canvasToBlob(canvas: HTMLCanvasElement, type = 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas could not be exported (likely CORS-tainted)'));
    }, type);
  });
}

/**
 * Composes a stack of HTMLImageElements onto a fresh `width × width`
 * canvas with each image stretched to fill. Used both for the visible
 * preview and (independently) for the authoritative Save render.
 */
function composeLayers(layers: readonly HTMLImageElement[], size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  for (const img of layers) {
    ctx.drawImage(img, 0, 0, size, size);
  }
  return canvas;
}

export function AvatarEditor({ editor, upload, items }: AvatarEditorProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const username = user?.username ?? null;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /**
   * Authoritative source layers for the current editor state. The
   * preview canvas is a side-effect render; Save reads this ref and
   * composes a fresh canvas, so a click that lands while the preview
   * useEffect is still in flight cannot result in a half-rendered PNG.
   */
  const layersRef = useRef<HTMLImageElement[]>([]);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);

  const itemsByItemId = useMemo(() => {
    const map = new Map<string, LitSquadInventoryEntry>();
    for (const entry of items) {
      if (entry.itemId) map.set(entry.itemId, entry);
    }
    return map;
  }, [items]);

  const baseEntry = editor.baseItemId ? (itemsByItemId.get(editor.baseItemId) ?? null) : null;

  // Resolve URIs eagerly. Computing inside the redraw effect would require
  // adding entry references to the dep array, and those reidentify
  // whenever the parent's items list changes (e.g. paginated load), even
  // when the chosen IDs are unchanged. Joining the IDs into a stable
  // string is the dep we actually want.
  const baseUri = useMemo(
    () => (baseEntry ? resolveLitSquadThumbnail(baseEntry) : null),
    [baseEntry],
  );
  const accessoryUris = useMemo(
    () =>
      editor.accessoryItemIds
        .map((id) => itemsByItemId.get(id))
        .map((e) => (e ? resolveLitSquadThumbnail(e) : null))
        .filter((u): u is string => u !== null && isSafeImageUri(u)),
    [editor.accessoryItemIds, itemsByItemId],
  );
  const uriKey = useMemo(
    () => `${baseUri ?? ''}|${accessoryUris.join('|')}`,
    [baseUri, accessoryUris],
  );

  // Keep the source-of-truth accessory entries for chip rendering.
  const accessoryEntries = useMemo(
    () =>
      editor.accessoryItemIds
        .map((id) => itemsByItemId.get(id))
        .filter((e): e is LitSquadInventoryEntry => e !== undefined),
    [editor.accessoryItemIds, itemsByItemId],
  );

  // Compose the preview canvas whenever the IDs (not array references) change.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;
    setDrawError(null);

    ctx.clearRect(0, 0, FULL_SIZE_PX, FULL_SIZE_PX);
    if (!baseUri || !isSafeImageUri(baseUri)) {
      layersRef.current = [];
      setDrawing(false);
      return;
    }

    setDrawing(true);
    const allUris: readonly string[] = [baseUri, ...accessoryUris];
    Promise.all(allUris.map(loadCrossOriginImage))
      .then((layers) => {
        if (cancelled) return;
        layersRef.current = layers;
        ctx.clearRect(0, 0, FULL_SIZE_PX, FULL_SIZE_PX);
        for (const img of layers) {
          ctx.drawImage(img, 0, 0, FULL_SIZE_PX, FULL_SIZE_PX);
        }
        setDrawing(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to compose preview';
        setDrawError(msg);
        setDrawing(false);
      });

    return () => {
      cancelled = true;
    };
    // Intentional: dep is the joined uriKey rather than the
    // baseUri/accessoryUris references. The references reidentify
    // whenever the parent's items list paginates, but uriKey only
    // changes when the underlying URIs do — which is what we want.
  }, [uriKey]);

  const handleSave = useCallback(async () => {
    if (!username) {
      toast({
        title: 'Sign-in required',
        description: 'You need to be signed in to save an avatar.',
        variant: 'destructive',
      });
      return;
    }

    const layers = layersRef.current;
    if (layers.length === 0) {
      toast({
        title: 'Not ready',
        description: 'Source images are still loading. Try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Authoritative full-size compose — independent of the preview
      // canvas state, so a Save click during a re-render is safe.
      const fullSizeCanvas = composeLayers(layers, FULL_SIZE_PX);
      const fullSizeBlob = await canvasToBlob(fullSizeCanvas, 'image/png');

      // Two-pass downscale: 512 → 256 → 128. Each halving averages four
      // source pixels per destination, which is closer to a Lanczos
      // filter than a single one-shot 4× downscale and preserves thin
      // outlines that LitSquad species artwork relies on.
      const intermediate = document.createElement('canvas');
      intermediate.width = MINI_INTERMEDIATE_PX;
      intermediate.height = MINI_INTERMEDIATE_PX;
      const interCtx = intermediate.getContext('2d');
      if (!interCtx) throw new Error('Intermediate canvas context unavailable');
      interCtx.imageSmoothingEnabled = true;
      interCtx.imageSmoothingQuality = 'high';
      interCtx.drawImage(fullSizeCanvas, 0, 0, MINI_INTERMEDIATE_PX, MINI_INTERMEDIATE_PX);

      const mini = document.createElement('canvas');
      mini.width = MINI_SIZE_PX;
      mini.height = MINI_SIZE_PX;
      const miniCtx = mini.getContext('2d');
      if (!miniCtx) throw new Error('Mini canvas context unavailable');
      miniCtx.imageSmoothingEnabled = true;
      miniCtx.imageSmoothingQuality = 'high';
      miniCtx.drawImage(intermediate, 0, 0, MINI_SIZE_PX, MINI_SIZE_PX);
      const thumbnailBlob = await canvasToBlob(mini, 'image/png');

      await upload.upload({ username, fullSizeBlob, thumbnailBlob });

      editor.resetToSaved({
        baseItemId: editor.baseItemId,
        accessoryItemIds: editor.accessoryItemIds,
      });

      toast({ title: 'Avatar saved', description: 'Your new avatar is live.' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save avatar';
      toast({ title: 'Save failed', description: msg, variant: 'destructive' });
    }
  }, [username, editor, upload, toast]);

  const handleCancel = useCallback(() => {
    editor.cancel();
  }, [editor]);

  const savedAvatarUrl = useMemo(() => {
    if (!username || !CDN_BASE_URL) return null;
    return `${CDN_BASE_URL.replace(/\/$/, '')}/dfe-avatars/Avatars/${encodeURIComponent(
      username,
    )}.png?v=${upload.avatarVersion}`;
  }, [username, upload.avatarVersion]);

  // Save is unsafe while images are still loading (canvas may be empty)
  // and pointless while not dirty or while a previous upload is in flight.
  const isSaveDisabled = !editor.baseItemId || !editor.isDirty || upload.loading || drawing;

  return (
    <Card className="overflow-hidden">
      <CardContent className="flex flex-col gap-4 p-4 md:flex-row">
        <div className="flex flex-col items-center gap-2">
          <div className="relative h-64 w-64 overflow-hidden rounded-md border bg-muted md:h-72 md:w-72">
            <canvas
              ref={canvasRef}
              width={FULL_SIZE_PX}
              height={FULL_SIZE_PX}
              className="h-full w-full"
              data-testid="avatar-editor-canvas"
            />
            {!editor.baseItemId && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                <UserCircle className="h-12 w-12" />
                <span>Pick a LitSquad character below to start</span>
              </div>
            )}
            {drawing && editor.baseItemId && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-background/40"
                data-testid="avatar-editor-loading"
              >
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {drawError && (
              <div className="absolute inset-x-0 bottom-0 bg-destructive/90 px-2 py-1 text-center text-xs text-destructive-foreground">
                {drawError}
              </div>
            )}
          </div>
          <div className="flex w-full gap-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaveDisabled}
              aria-label="Save avatar"
              className="flex-1"
            >
              {upload.loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={!editor.isDirty || upload.loading}
              aria-label="Discard avatar changes"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div>
            <h2 className="text-base font-semibold">Your avatar</h2>
            <p className="text-xs text-muted-foreground">
              {username ? (
                <>
                  Saved as <code className="font-mono">{username}</code>
                </>
              ) : (
                'Sign in to save an avatar'
              )}
            </p>
          </div>

          {accessoryEntries.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {accessoryEntries.map((entry) => (
                <AvatarEditorAccessoryChip
                  key={entry.itemId ?? entry.itemDefId}
                  entry={entry}
                  onRemove={editor.removeAccessory}
                />
              ))}
            </div>
          )}

          {savedAvatarUrl && (
            <div className="mt-auto flex items-center gap-3 border-t pt-3 text-xs text-muted-foreground">
              <span>Currently saved:</span>
              <img
                src={savedAvatarUrl}
                alt="Currently saved avatar"
                className="h-12 w-12 rounded-full border bg-muted object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
            </div>
          )}

          {upload.error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 px-2 py-1 text-xs text-destructive">
              {upload.error}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
